import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredPermissions?: string[]
  requiredRole?: string
}

export default function ProtectedRoute({ 
  children, 
  requiredPermissions = [], 
  requiredRole 
}: ProtectedRouteProps) {
  const { isAuthenticated, admin, loading } = useAuth()
  const location = useLocation()

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />
  }

  // Check if admin exists
  if (!admin) {
    return <Navigate to="/admin/login" replace />
  }

  // Check if admin is active
  if (!admin.is_active) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Account Inactive</h2>
          <p className="text-gray-600 mb-4">Your admin account has been deactivated.</p>
          <button
            onClick={() => window.location.href = '/admin/login'}
            className="btn-primary"
          >
            Return to Login
          </button>
        </div>
      </div>
    )
  }

  // Super Admin bypasses all permission checks
  if (admin.is_superuser) {
    return <>{children}</>
  }

  // Check role requirement
  if (requiredRole && admin.role !== requiredRole) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            You don't have the required role ({requiredRole}) to access this page.
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Current role: {admin.role || 'None'}
          </p>
          <button
            onClick={() => window.location.href = '/admin/dashboard'}
            className="btn-primary"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    )
  }

  // Check permissions requirement
  if (requiredPermissions.length > 0) {
    // Ensure admin has permissions_list property
    const permissions = admin.permissions_list || []
    
    const hasAllPermissions = requiredPermissions.every(permission =>
      permissions.includes(permission)
    )

    if (!hasAllPermissions) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
            <p className="text-gray-600 mb-4">
              You don't have the required permissions to access this page.
            </p>
            <div className="text-sm text-gray-500 mb-4">
              <p>Required: {requiredPermissions.join(', ')}</p>
              <p>Available: {permissions.length > 0 ? permissions.join(', ') : 'None'}</p>
              <p>Super admin: {admin.is_superuser ? 'Yes' : 'No'}</p>
            </div>
            <button
              onClick={() => window.location.href = '/admin/dashboard'}
              className="btn-primary"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      )
    }
  }

  return <>{children}</>
}
